import React, { useState } from "react";

import { Grid } from "@mui/material";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FaRegComment } from "react-icons/fa";
import { FiBookmark } from "react-icons/fi";
import { MdOutlineScreenShare } from "react-icons/md";
import { useHistory } from "react-router-dom";
import { Room } from "twilio-video";

import { PRIVATE_ROUTES } from "../../../constants";
import BookmarkService from "../../../services/bookmark.service";
import { Color } from "../../Form/Button";
import Modal from "../../Generic/Modal";
import useVideoContext from "../hooks/useVideoContext/useVideoContext";
import useStyles, { Badge } from "./styles";

const meetingEnd = require("../../../assets/img/meeting-end.svg");

const Leave = ({ room }: { room: Room | null }) => {
  const [commentsCount, setCommentsCount] = useState<number>();
  const [bookmarksCount, setBookmarksCount] = useState<number>();
  const { task, screenshots } = useVideoContext();

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const history = useHistory();

  const closeBrowser = () => {
    window.opener = null;
    window.open("", "_self");
    window?.top?.close();
    window.history.go(-1);
  };

  const goBackToApp = () => {
    if (room) room.disconnect();
    history.push(PRIVATE_ROUTES.QUOTES);
  };

  useQuery(
    ["bookmark.list", task?.id],
    () =>
      BookmarkService.getAll({
        taskId: task?.id,
      }),
    {
      enabled: !!task?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const bookmarksFiltered = response.data.filter((data) => !data.comments);
          const commentsFiltered = response.data.filter((data) => !!data.comments);

          setCommentsCount(commentsFiltered?.length);
          setBookmarksCount(bookmarksFiltered?.length);
        }
      },
    }
  );

  return (
    <div>
      <Modal
        open={true}
        showActionButtons
        data-cy="leaveVideoSurveyModal"
        actionButtonProps={{
          onCancel: () => closeBrowser(),
          onSave: () => goBackToApp(),
          customColorSave: Color.SECONDARY,
          textSave: translate("goBackToApp"),
          textCancel: translate("closeTab"),
          dataCySave: "goBackToAppButton",
          dataCyCancel: "closeTabButton",
        }}
        BackdropProps={{
          timeout: 500,
          style: {
            backgroundImage: `url(${require("../../../assets/img/background_video_survey.png")})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
          },
        }}
      >
        <Grid container justifyContent="space-between">
          <Grid item xs={6} justifyContent="center" alignItems="center">
            <Grid item>
              <span className={classes.bigText}>
                {translate("wellDoneOnTheMeetingWith")} {task?.job?.customer?.name || "Customer"}!
              </span>
            </Grid>

            <Grid item>
              <span className={classes.middleText}>
                {translate("donTForgetWhatYouHaveAvailableFromThisMeeting")}
              </span>
            </Grid>

            <Grid
              item
              container
              spacing={2}
              direction="row"
              alignItems="center"
              className={classes.gridConteinerInfoIcon}
            >
              <Grid item sm={12} md={6}>
                <div className={classes.gridItem}>
                  <MdOutlineScreenShare size={15} color="#308CDB" className="icon" />

                  <span className={classes.availableOptionsText}>{translate("screenSharing")}</span>
                </div>
              </Grid>

              <Grid item sm={12} md={6}>
                <div className={classes.gridItem}>
                  <ScreenshotMonitorIcon className={classes.icon} />

                  <span className={classes.availableOptionsText}>{translate("screenshots")}</span>

                  <Badge>{[...screenshots].length}</Badge>
                </div>
              </Grid>

              <Grid item sm={12} md={6}>
                <div className={classes.gridItem}>
                  <FaRegComment size={15} color="#308CDB" className="icon" />

                  <span className={classes.availableOptionsText}>{translate("comments")}</span>

                  <Badge data-cy="commentsCount">{commentsCount || 0}</Badge>
                </div>
              </Grid>

              <Grid item sm={12} md={6}>
                <div className={classes.gridItem}>
                  <FiBookmark size={15} color="#308CDB" className="icon" />

                  <span className={classes.availableOptionsText}>{translate("bookmarks")}</span>

                  <Badge data-cy="bookmarksCount">{bookmarksCount || 0}</Badge>
                </div>
              </Grid>
            </Grid>

            <Grid item>
              <span className={classes.smallText}>
                {translate("didYouLeaveTheMeetingByMistake")}
                <a
                  className={classes.link}
                  onClick={() => window.location.reload()}
                  data-cy="rejoinVideoSurveyLink"
                >
                  {translate("rejoinTheMeetingHere")}
                </a>
              </span>
            </Grid>
          </Grid>

          <Grid item xs={6} container justifyContent="center" alignItems="center">
            <img src={meetingEnd} alt="meeting-end" />
          </Grid>
        </Grid>
      </Modal>
    </div>
  );
};

export default Leave;
